const BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.operaitor.co/api/v1';

export interface SignatureField {
  label: string;
  type: 'signature' | 'date' | 'text' | 'initials';
  required: boolean;
}

export interface AgreementForm {
  id: string;
  name: string;
  description: string | null;
  required: boolean;
  sort_order: number;
  content: string;
  signature_fields?: SignatureField[];
}

/** A single conditional-visibility rule: show this field only when another field
 *  in the same form/item holds `equals` (a checkbox `true`, or a select option value).
 *  A hidden field is not required, not rendered, and omitted from the answers. */
export interface ShowIfCondition {
  field: string;
  equals: string | boolean;
}

export interface IntakeFormField {
  key: string;
  label: string;
  field_type: 'text' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'multiselect' | 'checkbox' | 'textarea' | 'address' | 'signature' | 'initials' | 'file_upload' | 'static_text';
  /** Optional single conditional-visibility rule; when absent the field always shows. */
  show_if?: ShowIfCondition | null;
  /** Read-only paragraph for `static_text` fields (the text lives here, not in `label`). */
  content?: string | null;
  required: boolean;
  placeholder: string | null;
  options: string[] | null;
  ehr_mapping: string | null;
  accepted_file_types?: string[] | null;
  max_file_size_mb?: number | null;
  /** A required checkbox the patient must affirm to submit (backend#1998). */
  must_be_true?: boolean;
  /** On a `text` field: the insurance-company typeahead (backend#3196). A picked
   *  payer's `stedi_id` is posted in the same item's answers under this key. */
  payer_id_key?: string | null;
}

export interface IntakeFormSection {
  key: string;
  label: string;
  sort_order: number;
  fields: IntakeFormField[];
}

export interface IntakeForm {
  insurance_status_no_relaxes_required?: boolean;
  sections: IntakeFormSection[];
}

export interface AppointmentType {
  key: string;
  name: string;
  duration_minutes: number | null;
  bookable_online: boolean;
}

export interface PracticeInfo {
  business_id?: string;
  name: string;
  logo_url?: string | null;          // top-level presigned S3 URL (new backend)
  phone_number?: string | null;
  timezone?: string;
  practice_name?: string;
  booking_config?: {
    logo_url?: string;               // relative S3 key (old backend, fallback)
    custom_fields?: unknown[];
  };
  appointment_types?: AppointmentType[];
  /** backend#2009: false when the bookable-service catalog is non-ready
   *  (uncurated/drifted/blocked). Absent on older backends — treat absent as available. */
  automated_booking_available?: boolean;
  /** backend#2009: server-owned patient-safe copy for the unavailable state — the same
   *  string the 403 on /slots and /book carries. null when ready, absent on older backends. */
  booking_unavailable_message?: string | null;
  agreement_forms?: AgreementForm[];
  accepted_insurances?: string[];
  address?: string | null;
  kiosk_enabled?: boolean;
}

export interface Slot {
  slot_id: string;
  date: string;
  time: string;
  datetime_iso: string;
  provider_id?: string;
  provider_name: string | null;
  operatory_id?: string;
  length_minutes: number | null;
}

/** operaitor/backend#1604 slice 1: the in-flow forms step is retired and `/verify` and
 *  `/book` now always answer `pending_todos: []`. The type and the two optional fields
 *  below stay only for wire compatibility with the v1 responses (ADR 010); nothing reads them. */
export interface PendingTodo {
  id: string;
  todo_type: string;
  status: string;
  appointment_id: string;
  created_at: string;
}

export interface PatientToken {
  patient_token: string;
  patient_name?: string;
  success?: boolean;
  message?: string;
  ehr_patient_id?: string | null;
  pending_todos?: PendingTodo[]; // backend#1604: always `[]`, kept for wire compatibility
}

export interface BookResult {
  success?: boolean;
  message?: string;
  appointment_id?: string;
  pending_todos?: PendingTodo[]; // backend#1604: always `[]`, kept for wire compatibility
}

export interface ApiError extends Error {
  status?: number;
  data?: unknown;
}

/** The closed error code the backend places in `detail` (form-package v2 endpoints), if any. */
export function apiErrorCode(err: unknown): string | undefined {
  const data = (err as ApiError | undefined)?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : undefined;
}

async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  token?: string
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: ApiError = new Error(data?.message || data?.detail || `Request failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as T;
}

export const getPracticeInfo = async (clientId: string): Promise<PracticeInfo> => {
  const data = await request<PracticeInfo>('GET', `/public/booking/${clientId}`);
  // Normalize: if logo_url is absent at top-level but present in booking_config, promote it
  if (!data.logo_url && data.booking_config?.logo_url) {
    data.logo_url = data.booking_config.logo_url;
  }
  return data;
};

export const identifyPatient = (clientId: string, phone: string): Promise<{ exists: boolean }> =>
  request<{ exists: boolean }>('POST', `/public/booking/${clientId}/identify`, { phone });

export const verifyPatient = (clientId: string, phone: string, dob: string): Promise<PatientToken> =>
  request<PatientToken>('POST', `/public/booking/${clientId}/verify`, { phone, date_of_birth: dob });

export const registerPatient = (clientId: string, data: unknown): Promise<PatientToken> =>
  request<PatientToken>('POST', `/public/booking/${clientId}/register`, data);

export interface SlotsResponse {
  slots?: Slot[];
  appointment_type?: string | null;
  from_cache?: boolean;
  // Set ONLY when `slots` is empty: the soonest date the EHR thinks is worth
  // trying (YYYY-MM-DD), so the picker can offer a jump instead of making the
  // patient click every day. A hint, not a promise — re-search it for real times.
  next_available_date?: string | null;
}

export const getSlots = (
  clientId: string,
  token: string,
  params: { appointment_type?: string; date?: string; provider_id?: string } = {}
): Promise<SlotsResponse | Slot[]> => {
  const qs = new URLSearchParams();
  if (params.appointment_type) qs.set('appointment_type', params.appointment_type);
  if (params.date) qs.set('date', params.date);
  if (params.provider_id) qs.set('provider_id', params.provider_id);
  const q = qs.toString() ? `?${qs}` : '';
  return request(`GET`, `/public/booking/${clientId}/slots${q}`, null, token);
};

// ---------------------------------------------------------------------------
// Campaign attribution (booking_visits) — PHI-free landing capture.
//
// On landing we POST the URL tracking params (GIC, FID, utm_*, gclid, …) to
// /visit and stash the returned opaque visit_id in sessionStorage. bookAppointment
// then sends it back so the backend can tie the campaign to the resulting
// appointment. Everything here is best-effort: attribution must never block or
// break booking.
// ---------------------------------------------------------------------------

const VISIT_ID_KEY = 'operaitor_visit_id';
// Query params the booking flow uses functionally — never sent as attribution.
const FUNCTIONAL_PARAMS = new Set(['prefill', 'token']);

export interface VisitResult {
  recorded: boolean;
  visit_id?: string | null;
}

export function getStoredVisitId(): string | undefined {
  try {
    return sessionStorage.getItem(VISIT_ID_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export const recordVisit = (
  clientId: string,
  payload: { tracking_params: Record<string, string>; referrer?: string; landing_path?: string }
): Promise<VisitResult> =>
  request<VisitResult>('POST', `/public/booking/${clientId}/visit`, payload);

/**
 * Capture the landing for campaign attribution. Idempotent per browser session
 * (guarded by sessionStorage) and fully best-effort — any failure is swallowed
 * so it can never interfere with the booking flow.
 */
export async function captureVisit(clientId: string): Promise<void> {
  try {
    if (getStoredVisitId()) return; // already captured this session
    const sp = new URLSearchParams(window.location.search);
    const tracking_params: Record<string, string> = {};
    sp.forEach((value, key) => {
      if (!FUNCTIONAL_PARAMS.has(key.toLowerCase())) tracking_params[key] = value;
    });
    const res = await recordVisit(clientId, {
      tracking_params,
      referrer: document.referrer || undefined,
      landing_path: window.location.pathname || undefined,
    });
    if (res.recorded && res.visit_id) {
      sessionStorage.setItem(VISIT_ID_KEY, res.visit_id);
    }
  } catch {
    /* best-effort: attribution must never block booking */
  }
}

export const bookAppointment = (
  clientId: string,
  token: string,
  slot: Slot,
  appointmentType: string,
  // Defaults to the visit_id captured at landing; sending it lets the backend
  // stamp this conversion onto the booking_visits row.
  visitId: string | undefined = getStoredVisitId()
): Promise<BookResult> =>
  request<BookResult>(
    'POST',
    `/public/booking/${clientId}/book`,
    {
      slot_id: slot.slot_id,
      appointment_type: appointmentType,
      appointment_date: slot.date,
      appointment_time: slot.time,
      provider_id: slot.provider_id,
      operatory_id: slot.operatory_id,
      slot_datetime: slot.datetime_iso,
      visit_id: visitId,
    },
    token
  );

// Short Links

/**
 * A V1 short link (`custom_form`, `form_package`, `prefill`): every lane it could open is retired
 * (operaitor/backend#1604 — slice 1 took the prefill forms-only flow, slice 2 the custom-form and
 * V1 package renderers). Kept in the union so a link minted before the cutover routes explicitly
 * to the expired screen instead of falling through as an unknown type. Once the backend half
 * ships, the resolver answers these types 404 `form_link_retired`, rendered with
 * expired-link recovery; a 200 with one of these types can only come from an older backend. Everything the
 * old flows read from the body (`token`, `form`, `forms`, `business`, the prefill fields) went
 * with the lanes; branding comes from `getPracticeInfo(client_id)`.
 */
export interface ShortLinkRetired {
  link_type: 'custom_form' | 'form_package' | 'prefill';
  client_id: string;
}

/** One member of a household link (operaitor/backend#2277, option B). Identity is
 *  deliberately first-name-only: enough to disambiguate "who is this for" without
 *  putting DOB or phone in a bearer-resolvable, forwardable payload (the PHI trade
 *  the issue flags for security review). Each member keeps their own per-patient
 *  `short_code`, so picking one opens the existing single-patient /f/{code} lane. */
export interface HouseholdMember {
  short_code: string;
  first_name: string;
  /** Forms/packages this member still has to fill (drives "…has forms to fill – N"). */
  forms_count: number;
  /** True for a dependent under 18 (backend#2277 phase 2), so the hub can label them. */
  is_minor?: boolean;
}

/** A single link texted once per phone that resolves to every household member with
 *  open forms, instead of one identical-looking link per patient. */
export interface ShortLinkHousehold {
  link_type: 'form_package_household';
  client_id: string;
  business: { name: string; logo_url: string | null };
  members: HouseholdMember[];
}

/** Locked v2 resolve (backend#form-verify): a form link is a bearer credential, so
 *  first contact returns only what the DOB verify screen needs — branding + how many
 *  forms wait — and deliberately withholds the token AND the form content until the
 *  patient confirms the pinned chart's date of birth via `verifyFormPackageV2`. */
export interface ShortLinkFormPackageV2Locked {
  link_type: 'form_package_v2';
  client_id: string;
  verification_required: true;
  package_instance_id: string;
  package_key: string;
  expires_at: string;
  forms_count: number;
  business: { name: string; logo_url: string | null };
}

export type ShortLinkResponse =
  | ShortLinkRetired
  | ShortLinkFormPackageV2
  | ShortLinkFormPackageV2Locked
  | ShortLinkHousehold;

// The short code is bearer-equivalent — resolving it returns the live form JWT (plus
// prefill PHI) — and a path segment is recorded by every hop that logs a request line
// (access log, ALB, WAF samples, browser history). The body reaches none of them
// (backend#2129). `GET /f/{short_code}` still exists as the deprecated compat transport.
export const resolveShortLink = (shortCode: string): Promise<ShortLinkResponse> =>
  request('POST', '/public/booking/f/resolve', { short_code: shortCode });

export type ReissueFormLinkResult =
  | { outcome: 'sent' | 'uncertain' }
  | { outcome: 'try_later'; retry_after: string };

const REISSUE_ERROR_CODES = [
  'verification_failed', 'too_many_attempts', 'reissue_limited', 'form_link_still_live',
  'form_package_consumed', 'no_single_upcoming_appointment', 'no_outstanding_obligation',
  'chart_unresolved', 'sms_unavailable', 'issuance_refused', 'sms_opted_out', 'reissue_failed',
];

export async function reissueFormLink(shortCode: string, phone: string, dob: string): Promise<ReissueFormLinkResult> {
  let raw: unknown;
  try {
    raw = await request('POST', '/public/booking/f/reissue', {
      short_code: shortCode, phone, date_of_birth: dob,
    });
  } catch (err) {
    const safe = statusOnlyError(err);
    const code = apiErrorCode(err);
    if (code && REISSUE_ERROR_CODES.includes(code)) safe.data = { detail: code };
    throw safe;
  }
  const body = raw as { outcome?: unknown; retry_after?: unknown } | null;
  if (body?.outcome === 'sent' || body?.outcome === 'uncertain') return { outcome: body.outcome };
  if (body?.outcome === 'try_later' && typeof body.retry_after === 'string'
    && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(body.retry_after)
    && Number.isFinite(Date.parse(body.retry_after))
    && new Date(body.retry_after.slice(0, 10)).toISOString().slice(0, 10) === body.retry_after.slice(0, 10)) {
    return { outcome: 'try_later', retry_after: body.retry_after };
  }
  throw statusOnlyError(undefined);
}

/** Confirm the pinned chart's DOB to unlock a v2 package (backend#form-verify).
 *  On success returns the full (token-bearing) package view; a wrong DOB throws a
 *  401 (`verification_failed`), and the link locks after a bounded number of misses
 *  (`too_many_attempts`, 429). `dob` is `YYYY-MM-DD`. */
export const verifyFormPackageV2 = (
  clientId: string,
  shortCode: string,
  dob: string,
): Promise<ShortLinkFormPackageV2> =>
  request('POST', `/public/booking/${clientId}/form-package/v2/verify`, {
    short_code: shortCode,
    date_of_birth: dob,
  });

/** Re-fetch an already-verified package with the token (resume path): a refresh
 *  within the tab restores the forms without re-prompting for DOB. */
export const getFormPackageV2View = (
  clientId: string,
  token: string,
): Promise<ShortLinkFormPackageV2> =>
  request('GET', `/public/booking/${clientId}/form-package/v2/view`, null, token);

// ---------------------------------------------------------------------------
// Form packages v2 (operaitor/backend#2049) — immutable package instances.
//
// A v2 link resolves to a frozen read model keyed by instance item id; the SPA
// never refetches mutable form definitions. Signatures and files are staged as
// uploads BEFORE submit and referenced by id; nothing is posted as base64.
// ---------------------------------------------------------------------------

export type V2FormKind = 'intake' | 'agreement' | 'custom' | 'insurance_update' | 'registration';

/** Closed staging vocabulary; mirrors the backend DB CHECK exactly. */
export type V2UploadKind =
  | 'signature'
  | 'insurance_card_front'
  | 'insurance_card_back'
  | 'photo_id_front'
  | 'photo_id_back';

/** Migration-087 snapshot of a legacy form row. Nullable columns may be absent. */
export interface ShortLinkFormPackageV2ItemContent {
  content?: string | null;                    // agreement text
  signature_fields?: SignatureField[] | null; // agreement signature fields
  form_template?: IntakeForm | null;          // intake / custom template
}

export interface ShortLinkFormPackageV2Item {
  instance_item_id: string;
  ordinal: number;
  form_id: string;
  form_revision_id: string;
  revision_number: number;
  form_key: string;
  form_kind: V2FormKind | string;
  name: string;
  description: string | null;
  required: boolean;
  content: ShortLinkFormPackageV2ItemContent | null;
}

export interface ShortLinkFormPackageV2 {
  link_type: 'form_package_v2';
  /** Present only on the UNLOCKED (post-verify) payload; the locked resolve omits it. */
  verification_required?: false;
  token: string;
  client_id: string;
  schema_version: number;
  package_instance_id: string;
  package_key: string;
  package_version: number;
  expires_at: string;
  items: ShortLinkFormPackageV2Item[];
  business: { name: string; logo_url: string | null };
  /** The live insurance check's bearer (backend#3197). Returned by `/verify` only when
   *  the chart had a DOB to match; never by kiosk links or `/view`. Memory only. */
  dob_proof_token?: string;
}

export interface V2UploadResult {
  upload_id: string;
  upload_kind: V2UploadKind;
  content_sha256: string;
  byte_size: number;
  expires_at: string;
  /** Optional vision extraction (backend#2063); absent today. */
  field_mapping?: Record<string, string> | null;
}

/** Stage one signature or card/ID image against a v2 package before submit. */
export async function stageFormPackageV2Upload(
  clientId: string,
  token: string,
  uploadKind: V2UploadKind,
  file: Blob,
  fileName: string,
): Promise<V2UploadResult> {
  const formData = new FormData();
  formData.append('upload_kind', uploadKind);
  formData.append('file', file, fileName);
  const res = await fetch(
    `${BASE}/public/booking/${clientId}/form-package/v2/uploads`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: ApiError = new Error(data.message || data.detail || `Upload failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as V2UploadResult;
}

export type V2AgreementSignatureFieldAnswer =
  | { label: string; type: 'signature'; upload_id: string }
  | { label: string; type: 'initials' | 'date' | 'text'; value: string }
  // An unanswered OPTIONAL entry keeps its index (backend#1998 validates the array
  // by position against the revision's signature_fields).
  | { label: string; type: 'signature' | 'initials' | 'date' | 'text' };

export interface V2AgreementAnswer {
  agreed: true;
  signature_fields: V2AgreementSignatureFieldAnswer[];
}

export type V2FieldAnswer = string | string[] | boolean | { upload_id: string };
export type V2FormAnswer = Record<string, V2FieldAnswer>;

export interface FormPackageV2Consent {
  name: string;
  signature_upload_id: string;
}

/** A form-builder agreement: `agreed` plus its own field answers, keyed like an intake item. */
export type V2TemplateAgreementAnswer = { agreed: true } & V2FormAnswer;

export interface FormPackageV2SubmitPayload {
  request_idempotency_key: string;
  consent?: FormPackageV2Consent;
  answers: Record<string, V2AgreementAnswer | V2TemplateAgreementAnswer | V2FormAnswer>;
  upload_ids: string[];
  /** Each policy slot's last live insurance check (backend#3198), only for a slot
   *  whose answers are still the checked ones; absent when there is none. The
   *  worker validates every reference and runs its own check for a stale one. */
  insurance_checks?: Partial<Record<PolicySlot, { check_id: string }>>;
}

export interface FormPackageV2SubmitResult {
  submission_id: string;
  status: 'accepted';
  accepted_at: string;
  idempotent_replay: boolean;
}

export const submitFormPackageV2 = (
  clientId: string,
  token: string,
  payload: FormPackageV2SubmitPayload,
): Promise<FormPackageV2SubmitResult> =>
  request('POST', `/public/booking/${clientId}/form-package/v2/submit`, payload, token);

// ---------------------------------------------------------------------------
// Insurance on v2 forms: the insurance-company typeahead (backend#3196) and the
// live insurance check (backend#3197). Both are best-effort for the patient:
// every failure falls back to today's form. Queries and typed policy details
// travel only in these POST bodies; a failure is rethrown with its status and
// nothing else, since a 422 body echoes the submitted values back.
// ---------------------------------------------------------------------------

export interface FormPayer {
  display_name: string;
  stedi_id: string;
}

export interface FormPayerSearchResult {
  payers: FormPayer[];
  fallback_to_free_text: boolean;
}

export type InsuranceCheckOutcome =
  | 'active'
  | 'inactive'
  | 'check_member_id'
  | 'check_name_or_dob'
  | 'not_found'
  | 'unavailable'
  | 'limit_reached';

// The submission posts a check_id back as a UUID (backend#3198), and anything else
// there is a 422 on every attempt, so an id that is not one is dropped here.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const INSURANCE_CHECK_OUTCOMES: readonly InsuranceCheckOutcome[] = [
  'active', 'inactive', 'check_member_id', 'check_name_or_dob', 'not_found', 'unavailable', 'limit_reached',
];

export type PolicySlot = 'primary' | 'secondary';

export interface FormInsuranceCheckRequest {
  policy_slot: PolicySlot;
  relationship_to_policyholder: 'self' | 'spouse' | 'child' | 'other';
  payer_id: string;
  member_id: string;
  // A "Me" check leaves all three out when the form never asks the patient's
  // name or DOB; the backend then reads them from the patient record (backend#3519).
  policyholder_first_name?: string;
  policyholder_last_name?: string;
  /** YYYY-MM-DD */
  policyholder_date_of_birth?: string;
}

export type InsuranceUnavailableReason = 'provider_refused' | 'payer_unavailable' | 'check_failed';

const INSURANCE_UNAVAILABLE_REASONS: readonly InsuranceUnavailableReason[] = [
  'provider_refused', 'payer_unavailable', 'check_failed',
];

export interface FormInsuranceCheckResult {
  outcome: InsuranceCheckOutcome;
  check_id: string | null;
  unavailable_reason?: InsuranceUnavailableReason;
}

function statusOnlyError(err: unknown): ApiError {
  const status = (err as ApiError | undefined)?.status;
  const out: ApiError = new Error(status ? `Request failed: ${status}` : 'Request failed');
  if (status) out.status = status;
  return out;
}

/** Search the payer list for the typeahead. Bearer: the form link token. A body this
 *  build cannot read is reported as a fallback to free text, never as an error. */
export async function searchFormPackageV2Payers(
  clientId: string,
  token: string,
  query: string,
): Promise<FormPayerSearchResult> {
  let raw: unknown;
  try {
    raw = await request('POST', `/public/booking/${clientId}/form-package/v2/payers/search`, { query }, token);
  } catch (err) {
    throw statusOnlyError(err);
  }
  const body = raw as { payers?: unknown; fallback_to_free_text?: unknown } | null;
  if (!body || !Array.isArray(body.payers) || typeof body.fallback_to_free_text !== 'boolean') {
    return { payers: [], fallback_to_free_text: true };
  }
  const payers = body.payers.filter((p): p is FormPayer => (
    !!p && typeof p.display_name === 'string' && p.display_name.trim() !== ''
    && typeof p.stedi_id === 'string' && p.stedi_id.trim() !== ''
  ));
  return { payers, fallback_to_free_text: body.fallback_to_free_text };
}

/** Check one policy with the patient's insurer while they fill in the form.
 *  Bearer: the `dob_proof_token` from `/verify`, never the link token. An outcome
 *  this build does not know reads as `unavailable`, and a check_id that is not a
 *  UUID as none. */
export async function checkFormPackageV2Insurance(
  clientId: string,
  dobProofToken: string,
  body: FormInsuranceCheckRequest,
): Promise<FormInsuranceCheckResult> {
  let raw: unknown;
  try {
    raw = await request('POST', `/public/booking/${clientId}/form-package/v2/insurance-check`, body, dobProofToken);
  } catch (err) {
    throw statusOnlyError(err);
  }
  const res = raw as { outcome?: unknown; check_id?: unknown; unavailable_reason?: unknown } | null;
  const outcome = INSURANCE_CHECK_OUTCOMES.find((o) => o === res?.outcome) ?? 'unavailable';
  const checkId = typeof res?.check_id === 'string' && UUID_PATTERN.test(res.check_id) ? res.check_id : null;
  const reason = res?.outcome === 'unavailable'
    ? INSURANCE_UNAVAILABLE_REASONS.find((r) => r === res.unavailable_reason) : undefined;
  return { outcome, check_id: checkId, ...(reason ? { unavailable_reason: reason } : {}) };
}

// ---------------------------------------------------------------------------
// Drafts — server-side resume (backend#… draft endpoints). The whole flow state
// is stored as one opaque JSON blob keyed by the link token, so a refresh or a
// return on a new device restores where the patient left off. This is NOT
// browser storage: no PHI is written to the device (see the FormPackageV2Renderer
// note); the answers live on the server behind the bearer, exactly like the
// staged uploads they reference. Uploads are carried by `upload_id` only — never
// base64 — since the staged rows already live server-side.
// ---------------------------------------------------------------------------

/** One staged signature/file as persisted in a draft: `previewUrl` is dropped
 *  (in-memory only), so a resumed signature validates but its thumbnail is blank
 *  until redrawn. */
export interface FormPackageV2DraftUpload {
  uploadId: string;
  uploadKind: V2UploadKind;
  byteSize: number;
  fileName?: string;
}

/** The renderer's serializable flow state. Canvas refs and preview data-URLs are
 *  deliberately excluded; uploads are referenced by id via `uploads`. */
export interface FormPackageV2Draft {
  /** Draft-shape version, so a future change can ignore an incompatible blob. */
  v: 1;
  step: number;
  agreedItemIds: string[];
  agreementFieldValues: Record<string, Record<number, string>>;
  formValues: Record<string, Record<string, unknown>>;
  consentName: string;
  uploads: Record<string, FormPackageV2DraftUpload>;
}

// The backend wraps these two endpoints in the `{success, data}` envelope (unlike
// resolve/submit, which return the object directly), so unwrap `.data` here.
interface V2DraftEnvelope {
  success?: boolean;
  data?: { draft?: FormPackageV2Draft | null; draft_updated_at?: string | null };
}

export interface FormPackageV2DraftResult {
  draft: FormPackageV2Draft | null;
  draftUpdatedAt: string | null;
}

export const saveFormPackageV2Draft = (
  clientId: string,
  token: string,
  draft: FormPackageV2Draft,
): Promise<unknown> =>
  request('PUT', `/public/booking/${clientId}/form-package/v2/draft`, { draft }, token);

export async function getFormPackageV2Draft(
  clientId: string,
  token: string,
): Promise<FormPackageV2DraftResult> {
  const res = await request<V2DraftEnvelope>('GET', `/public/booking/${clientId}/form-package/v2/draft`, null, token);
  const data = res?.data ?? {};
  const draft = data.draft && data.draft.v === 1 ? data.draft : null;
  return { draft, draftUpdatedAt: data.draft_updated_at ?? null };
}

// --- Kiosk ---

export interface KioskV2PackageOption {
  package_key: string;
  name: string;
  description?: string | null;
}

// operaitor/backend#2619 — what the obligation policy says the checked-in patient
// owes. Evaluated-empty is check-in complete. With no menu, `not_evaluated`
// (no appointment in the window or no rules configured) is also complete, while
// `unavailable` directs the patient to the front desk. With a menu, both of those
// states show the menu only and make no claim about what is owed.
export type KioskDueState = 'evaluated' | 'not_evaluated' | 'unavailable';
const DUE_STATES: readonly KioskDueState[] = ['evaluated', 'not_evaluated', 'unavailable'];

export interface KioskV2DueOption {
  decision_key: string;
  package_key: string;
  name: string;
  description?: string | null;
}

export interface KioskDueList {
  due_state: KioskDueState;
  due_reason?: string | null;
  due: KioskV2DueOption[];
}

export interface KioskV2Session {
  mode: 'form_package_v2';
  kiosk_token: string;
  patient_name?: string | null;
  packages: KioskV2PackageOption[];
  // Absent on a backend older than #2619; `normalizeDueList` reads that as
  // `unavailable`: show the menu if present, otherwise front-desk guidance.
  due_state?: KioskDueState;
  due_reason?: string | null;
  due?: KioskV2DueOption[];
}

export interface KioskObligationIssueResult {
  decision_key: string;
  package_key: string;
  package_instance_id: string;
  form_url: string;
  expires_at: string;
  // True when the link is the one the SMS chase already sent: the fill consumes
  // the chase's package instead of a second one.
  resumed: boolean;
}

function validDueOption(value: unknown): value is KioskV2DueOption {
  if (!value || typeof value !== 'object') return false;
  const option = value as Partial<KioskV2DueOption>;
  return typeof option.decision_key === 'string'
    && option.decision_key.length > 0
    && typeof option.package_key === 'string'
    && option.package_key.length > 0
    && typeof option.name === 'string';
}

/**
 * The due list off a check-in or `/due` body, failed closed: anything that is not
 * one of the three known states with a well-formed list reads as `unavailable`,
 * so a malformed or older response can never render as "nothing to fill out".
 */
export function normalizeDueList(raw: unknown): KioskDueList {
  const body = (raw && typeof raw === 'object' ? raw : {}) as Partial<KioskDueList>;
  const state = body.due_state;
  if (!state || !DUE_STATES.includes(state) || !Array.isArray(body.due)) {
    return { due_state: 'unavailable', due_reason: 'unrecognized_response', due: [] };
  }
  const due = body.due.filter(validDueOption);
  if (due.length !== body.due.length) {
    return { due_state: 'unavailable', due_reason: 'unrecognized_response', due: [] };
  }
  return {
    due_state: state,
    due_reason: typeof body.due_reason === 'string' ? body.due_reason : null,
    due,
  };
}

export interface KioskPackageIssueResult {
  package_key: string;
  package_instance_id: string;
  form_url: string;
  expires_at: string;
}

// --- Kiosk (booking path) endpoints ---

/**
 * Kiosk check-in. Since operaitor/backend#1604 slice 2 the only 200 shape is the
 * form-package-v2 session (`mode`, `kiosk_token`, `packages`), including an empty
 * menu. Genuine unavailability gets 503 `kiosk_forms_unavailable`. An unsupported
 * 200 body (including the retired V1 signing-session shape) fails closed with that
 * same 503 so the tablet shows "Forms are temporarily unavailable".
 */
export async function kioskCheckIn(
  clientId: string,
  data: { phone: string; date_of_birth: string }
): Promise<KioskV2Session> {
  const s = await request<Partial<KioskV2Session> | null>('POST', `/public/booking/${clientId}/signing/kiosk`, data);
  if (
    !s || typeof s !== 'object'
    || s.mode !== 'form_package_v2'
    || typeof s.kiosk_token !== 'string'
    || !Array.isArray(s.packages)
  ) {
    const err: ApiError = new Error('kiosk_forms_unavailable');
    err.status = 503;
    err.data = { detail: 'kiosk_forms_unavailable' };
    throw err;
  }
  return s as KioskV2Session;
}

export const issueKioskPackage = (
  clientId: string,
  kioskToken: string,
  packageKey: string
): Promise<KioskPackageIssueResult> =>
  request(
    'POST',
    `/public/booking/${clientId}/signing/kiosk/package`,
    { package_key: packageKey },
    kioskToken
  );

// Re-reads what the checked-in patient still owes (operaitor/backend#2619) — called
// when the patient returns to the hub after a fill, so a completed form drops off
// the screen without a second identity check.
export const kioskDue = async (
  clientId: string,
  kioskToken: string
): Promise<KioskDueList> =>
  normalizeDueList(
    await request<unknown>('POST', `/public/booking/${clientId}/signing/kiosk/due`, undefined, kioskToken)
  );

// Opens (or resumes) one form the policy says is due. The server re-decides before
// honouring the key: a key that is no longer due is 409 `kiosk_obligation_not_due`,
// on which the hub re-reads the list.
export const issueKioskObligation = (
  clientId: string,
  kioskToken: string,
  decisionKey: string
): Promise<KioskObligationIssueResult> =>
  request(
    'POST',
    `/public/booking/${clientId}/signing/kiosk/obligation`,
    { decision_key: decisionKey },
    kioskToken
  );

// Revokes the kiosk session server-side (denylists the bearer's jti for its
// remaining lifetime). Idempotent and best-effort by design: on any failure
// the token's expiry remains the backstop, so callers fire-and-forget.
export const endKioskSession = (
  clientId: string,
  kioskToken: string
): Promise<{ success: boolean }> =>
  request('POST', `/public/booking/${clientId}/signing/kiosk/end`, undefined, kioskToken);
